import { useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { v1 as uuidv1 } from 'uuid';
import { gql } from '@yi/core';

import { getSearchInsightsCookies } from '~/common';
import { TrackingType } from '~/src/common/consumption/completion-snackbar';

import type { Content } from '../types';

import { OnMinuteConsumedCbParams, useMinuteConsumed } from '../../media-lifecycle-hooks';
import { extractContentId, extractTeacher, extractTitle } from '../content-utils';

export function useOnMinuteConsumedAnalytics({
  content,
  trackingType,
  indexName,
  contentTag,
}: {
  content: Content;
  contentTag?: string | null;
  trackingType?: TrackingType;
  indexName: string;
}) {
  const [logConsumptionTracking] = useMutation<
    gql.typesClient.Mutate_LogConsumptionTracking_Base,
    gql.typesClient.Mutate_LogConsumptionTracking_BaseVariables
  >(gql.Mutate_LogConsumptionTracking_Base, {
    // If graphQL error, onError is necessary for mutation promise to resolve
    onError: error => {
      console.warn(error);
    },
  });

  const rowId = useRef(uuidv1());
  const contentId = extractContentId(content);
  const title = extractTitle(content);
  const teacher = extractTeacher(content);
  const teacherFullName = teacher?.full_name || '';

  const minuteConsumedHandler = useCallback(
    ({ mediaDurationMinutes, totalMinutesConsumed }: OnMinuteConsumedCbParams) => {
      const { queryID } = getSearchInsightsCookies();
      if (trackingType) {
        logConsumptionTracking({
          variables: {
            row_id: rowId.current,
            class_id: contentId,
            minutes: totalMinutesConsumed,
            duration: mediaDurationMinutes,
            type: trackingType,
            searchInsightsIndexName: indexName,
            searchInsightsQueryID: queryID,
            contentTitle: title || '',
            contentAuthor: teacherFullName,
            contentTag: contentTag || '',
          },
        });
      }
    },
    [
      contentId,
      indexName,
      logConsumptionTracking,
      contentTag,
      teacherFullName,
      title,
      trackingType,
    ],
  );

  useMinuteConsumed(minuteConsumedHandler);
}
